import { useId } from 'react'
import PropTypes from 'prop-types'
import { ASSISTANTS, SPECIALIST_ASSISTANTS } from '../data/assistants'
import PublicNav from './PublicNav'
import Footer from './Footer'

const stars = [
  [4, 4, true, true],
  [4, 44, true],
  [36, 22],
  [50, 146, true, true],
  [64, 43, true, true],
  [76, 30, true],
  [101, 116],
  [140, 36, true],
  [149, 134],
  [162, 74, true],
  [171, 96, true, true],
  [210, 56, true, true],
  [235, 90],
  [275, 82, true, true],
  [306, 6],
  [307, 64, true, true],
  [380, 68, true],
  [380, 108, true, true],
  [391, 148, true, true],
  [405, 18, true],
  [412, 86, true, true],
  [426, 210, true, true],
  [427, 56, true, true],
  [538, 138],
  [563, 88, true, true],
  [611, 154, true, true],
  [637, 150],
  [651, 146, true],
  [682, 70, true, true],
  [683, 128],
  [781, 82, true, true],
  [785, 158, true],
  [832, 146, true, true],
  [852, 89],
]

const constellations = [
  [
    [247, 103],
    [261, 86],
    [307, 104],
    [357, 36],
  ],
  [
    [586, 120],
    [516, 100],
    [491, 62],
    [440, 107],
    [477, 180],
    [516, 100],
  ],
  [
    [733, 100],
    [803, 120],
    [879, 113],
    [823, 164],
    [803, 120],
  ],
]

function Star({ blurId, point: [cx, cy, dim, blur] }) {
  const delay = Math.random() * 2
  const duration = Math.random() * 2 + 2

  return (
    <circle
      cx={cx}
      cy={cy}
      r={1}
      style={{
        transformOrigin: `${cx / 16}rem ${cy / 16}rem`,
        animation: `star-fade-in 4s ${delay}s forwards, ${dim ? 'star-twinkle-dim' : 'star-twinkle'} ${duration}s ${delay + 4}s infinite alternate`,
      }}
      filter={blur ? `url(#${blurId})` : undefined}
    />
  )
}

Star.propTypes = {
  blurId: PropTypes.string.isRequired,
  point: PropTypes.array.isRequired,
}

function Constellation({ points, blurId }) {
  const uniquePoints = points.filter(
    (point, pointIndex) =>
      points.findIndex((p) => String(p) === String(point)) === pointIndex,
  )
  const isFilled = uniquePoints.length !== points.length
  const drawDelay = Math.random() * 3 + 2
  const drawDuration = 5

  return (
    <>
      <path
        stroke="white"
        strokeOpacity="0.2"
        strokeDasharray={1}
        strokeDashoffset={1}
        pathLength={1}
        fill={isFilled ? 'rgb(255 255 255 / 0.02)' : 'transparent'}
        d={`M ${points.join('L')}`}
        style={{
          animation: `constellation-draw ${drawDuration}s ${drawDelay}s forwards${isFilled ? `, constellation-fill 1s ${drawDelay + drawDuration}s forwards` : ''}`,
          visibility: 'hidden',
        }}
      />
      {uniquePoints.map((point, pointIndex) => (
        <Star key={pointIndex} point={point} blurId={blurId} />
      ))}
    </>
  )
}

Constellation.propTypes = {
  points: PropTypes.array.isRequired,
  blurId: PropTypes.string.isRequired,
}

function HorizontalStarField({ className }) {
  const blurId = useId()

  return (
    <svg
      viewBox="0 0 881 211"
      fill="white"
      aria-hidden="true"
      className={`pointer-events-none absolute w-220.25 origin-top-left rotate-0 overflow-visible opacity-70 ${className}`}
    >
      <defs>
        <filter id={blurId}>
          <feGaussianBlur in="SourceGraphic" stdDeviation=".5" />
        </filter>
      </defs>
      {constellations.map((points, constellationIndex) => (
        <Constellation
          key={constellationIndex}
          points={points}
          blurId={blurId}
        />
      ))}
      {stars.map((point, pointIndex) => (
        <Star key={pointIndex} point={point} blurId={blurId} />
      ))}
    </svg>
  )
}

HorizontalStarField.propTypes = {
  className: PropTypes.string,
}

function TopGlow() {
  const id = useId()

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg
        className="absolute -top-[20%] left-0 h-[250%] w-full"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={`${id}-glow`} cy="0%">
            <stop offset="0%" stopColor="rgba(56, 189, 248, 0.5)" />
            <stop offset="40%" stopColor="rgba(0, 71, 255, 0.15)" />
            <stop offset="100%" stopColor="rgba(10, 14, 23, 0)" />
          </radialGradient>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill={`url(#${id}-glow)`}
        />
      </svg>
    </div>
  )
}

const Homepage = ({ onLogin, onShowFeatures, onShowPricing, onShowAbout, onShowContact, onShowPrivacy, onShowTerms, isActive = true }) => {

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header Section */}
      <div className="relative overflow-visible">
        <TopGlow />
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full">
            <HorizontalStarField className="top-14" />
          </div>
        </div>
        
        <PublicNav 
          onShowFeatures={() => {
            onShowFeatures()
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
          }}
          onShowPricing={onShowPricing}
          onLogin={onLogin}
          onBack={() => {}}
          activeProduct={false}
          activeHome={true}
        />

        <header className="pt-40 pb-24 relative z-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="font-display text-5xl font-light tracking-tight text-white sm:text-7xl mb-6">
                Validate ideas, create{' '}
                <span className="text-sky-300">opportunities</span>
              </h1>
              <p className="mt-8 text-2xl/10 text-gray-300 max-w-3xl mx-auto">
                Opptora is an <span className="text-sky-300">AI-powered ideation platform</span> that helps you validate and develop 
                your business ideas with market research, opportunity scoring, and 
                actionable insights.
              </p>
              <div className="mt-10">
                <button 
                  onClick={() => onLogin('register')}
                  className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-6 py-3 text-base font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30 shadow-lg"
                >
                  Sign Up for Free
                </button>
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Problem Section */}
      <div className="relative py-24 bg-gray-900/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-12">
              Why <span className="text-sky-300">validation</span> matters
            </h2>
            
            {/* Stats Section */}
            <div className="mx-auto max-w-5xl mb-12">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 p-8">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="text-5xl font-bold text-sky-300 mb-2 relative z-10">90%</div>
                  <div className="text-sm text-gray-400 relative z-10">Startups fail</div>
                </div>
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 p-8">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="text-5xl font-bold text-sky-300 mb-2 relative z-10">70%</div>
                  <div className="text-sm text-gray-400 relative z-10">Corporate innovation projects fail</div>
                </div>
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 p-8">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="text-5xl font-bold text-sky-300 mb-2 relative z-10">#1</div>
                  <div className="text-sm text-gray-400 relative z-10">Reason for failure: No market need</div>
                </div>
              </div>
            </div>
            
            <p className="text-2xl/10 text-gray-300">
              Most startups fail because they build products nobody wants. Traditional validation 
              requires expensive specialists—Market Researchers, Business Analysts, UX Designers, 
              Technical Architects—costing hundreds of thousands annually.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">
              Opptora <span className="text-sky-300">flips the script</span>
            </h2>
            <p className="text-2xl/10 text-gray-300">
              All the expertise you need. One platform. Always available. At a fraction of the cost.
            </p>
          </div>
          
          <div className="mx-auto max-w-5xl">
            {/* First Row - 3 Features */}
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 mb-8">
              {[
                { 
                  icon: <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-cyan-500 to-cyan-600',
                  title: 'AI-powered Opportunity analysis', 
                  desc: 'Market research experts evaluate market demand, competition and help you form your USP.' 
                },
                { 
                  icon: <path d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-teal-500 to-teal-600',
                  title: 'AI-powered Go-to-market strategy', 
                  desc: 'Commercial strategists collaborate with you to clarify your audience, positioning, and market approach.' 
                },
                { 
                  icon: <path d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-orange-500 to-orange-600',
                  title: 'AI-powered Business case creation', 
                  desc: 'Financial architects build your business case, including forecasts, funding plans, and ROI analysis.' 
                },
              ].map((feature, idx) => (
                <div key={idx} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 p-6 hover:outline-sky-500/50 transition-all">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg mb-4 relative z-10`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-white">
                      {feature.icon}
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2 relative z-10">{feature.title}</h3>
                  <p className="text-base text-gray-400 relative z-10">{feature.desc}</p>
                </div>
              ))}
            </div>
            
            {/* Second Row - 2 Features Centered */}
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-2 max-w-3xl mx-auto mb-8">
              {[
                { 
                  icon: <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-fuchsia-500 to-fuchsia-600',
                  title: 'AI-powered UX design', 
                  desc: 'UX designers transform your idea into intuitive, human-centered experiences.' 
                },
                { 
                  icon: <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-indigo-500 to-indigo-600',
                  title: 'AI-powered MVP development', 
                  desc: 'Development assistants build, test, and help you launch your minimum viable product to validate market fit.' 
                },
              ].map((feature, idx) => (
                <div key={idx} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 p-6 hover:outline-sky-500/50 transition-all">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg mb-4 relative z-10`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-white">
                      {feature.icon}
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2 relative z-10">{feature.title}</h3>
                  <p className="text-base text-gray-400 relative z-10">{feature.desc}</p>
                </div>
              ))}
            </div>
            
            {/* Third Row - 3 Features (Platform Tools) */}
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
              {[
                { 
                  icon: <path d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-green-500 to-green-600',
                  title: 'Interactive canvas', 
                  desc: 'Collaborate with AI on a visual whiteboard to develop and refine your ideas.' 
                },
                { 
                  icon: <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-emerald-500 to-emerald-600',
                  title: 'Task management', 
                  desc: 'Track progress from idea to execution with AI-suggested next steps.' 
                },
                { 
                  icon: <path d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" strokeLinecap="round" strokeLinejoin="round" />,
                  color: 'from-sky-500 to-sky-600',
                  title: 'Always available', 
                  desc: '24/7 access to your AI team—no waiting, no scheduling, no costly retainers.' 
                },
              ].map((feature, idx) => (
                <div key={idx} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-800/30 to-gray-800/30 shadow-none outline -outline-offset-1 outline-white/5 p-6 hover:outline-sky-500/30 transition-all">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg mb-4 relative z-10 opacity-80`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-white">
                      {feature.icon}
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white/90 mb-2 relative z-10">{feature.title}</h3>
                  <p className="text-base text-gray-400 relative z-10">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Meet Your Team Section */}
      <div className="relative py-24 bg-gray-900/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center mb-20">
            <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">
              Meet <span className="text-sky-300">the team</span>
            </h2>
          </div>
          
          {/* Auri - Your Guide - Hero Layout */}
          <div className="mx-auto max-w-5xl mb-20">
            <div className="relative rounded-3xl bg-gradient-to-br from-sky-800/40 via-sky-700/30 to-sky-900/40 shadow-2xl outline -outline-offset-1 outline-sky-400/40 p-12 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-300/60 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-sky-500/10 via-transparent to-transparent rounded-3xl pointer-events-none"></div>
              <div className="absolute -top-20 -right-20 w-80 h-80 bg-sky-400/10 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-sky-600/10 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-12">
                {/* Auri Image */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-64 h-64 flex items-center justify-center">
                      <img 
                        src={ASSISTANTS.auri.poses.default} 
                        alt={ASSISTANTS.auri.name}
                        className="w-full h-full object-contain drop-shadow-2xl"
                        style={{ imageRendering: 'crisp-edges' }}
                      />
                    </div>
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-sky-400 text-gray-900 text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                      YOUR GUIDE
                    </div>
                  </div>
                </div>
                
                {/* Auri Description */}
                <div className="flex-1 text-center lg:text-left">
                  <h3 className="font-display text-4xl font-light tracking-tight text-white mb-3">
                    Meet {ASSISTANTS.auri.name}
                  </h3>
                  <p className="text-xl text-sky-300 font-medium mb-4">{ASSISTANTS.auri.title}</p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    For each idea you have, she'll assemble the perfect AI team to take it forward. 
                    She'll connect you with the right specialists at exactly the right time.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Team Heading */}
          <div className="mx-auto max-w-3xl text-center mb-28">
            <h3 className="font-display text-3xl font-light tracking-tight text-white mb-4">
              Meet <span className="text-sky-300">the specialists</span>
            </h3>
            <p className="text-xl text-gray-400">
              Auri will connect you with these experts as your idea progresses
            </p>
          </div>
          
          {/* Specialist Team */}
          <div className="mx-auto max-w-5xl">
            {/* First Row - 3 Specialists */}
            <div className="grid grid-cols-1 gap-24 sm:gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 mb-8">
              {SPECIALIST_ASSISTANTS.slice(0, 3).map((assistant) => (
                <div key={assistant.id} className="relative rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 pt-40 sm:pt-32 pb-6 px-6 text-center">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  
                  <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-48 h-48 flex items-center justify-center">
                    <img 
                      src={assistant.poses.default} 
                      alt={assistant.name}
                      className="w-full h-full object-contain drop-shadow-2xl"
                      style={{ imageRendering: 'crisp-edges' }}
                    />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-2 relative z-10">{assistant.name}</h3>
                  <p className="text-base text-sky-300 mb-2 relative z-10">{assistant.title}</p>
                  <p className="text-base text-gray-400 relative z-10">{assistant.subtitle}</p>
                </div>
              ))}
            </div>
            
            {/* Second Row - 2 Specialists Centered */}
            <div className="grid grid-cols-1 gap-24 sm:gap-8 sm:grid-cols-2 md:grid-cols-2 max-w-3xl mx-auto mt-24 sm:mt-24">
              {SPECIALIST_ASSISTANTS.slice(3).map((assistant) => (
                <div key={assistant.id} className="relative rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 pt-40 sm:pt-32 pb-6 px-6 text-center">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  
                  <div className={`absolute -top-20 left-1/2 -translate-x-1/2 ${assistant.id === 'vera' ? 'w-52 h-52' : 'w-48 h-48'} flex items-center justify-center`}>
                    <img 
                      src={assistant.poses.default} 
                      alt={assistant.name}
                      className="w-full h-full object-contain drop-shadow-2xl"
                      style={{ imageRendering: 'crisp-edges' }}
                    />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-2 relative z-10">{assistant.name}</h3>
                  <p className="text-base text-sky-300 mb-2 relative z-10">{assistant.title}</p>
                  <p className="text-base text-gray-400 relative z-10">{assistant.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* How It Works CTA Section */}
      <div className="relative py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-12">
            Want to see <span className="text-sky-300">how it all works</span>?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Explore the platform features and discover how your AI team helps validate and develop your ideas
          </p>
          <button 
            onClick={() => {
              onShowFeatures()
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
            }}
            className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-8 py-3.5 text-lg font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30 transition-all shadow-lg"
          >
            How It Works
          </button>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-24 bg-gray-900/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 p-12 text-center">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
            <div className="absolute top-0 left-0 w-64 h-32 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 right-0 w-48 h-24 bg-gradient-to-tl from-white/10 via-transparent to-transparent rounded-br-3xl pointer-events-none"></div>
            <h2 className="font-display text-3xl font-light tracking-tight text-white sm:text-4xl mb-4 relative z-10">
              Ready to <span className="text-sky-300">validate your ideas</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto relative z-10">
              Validate fast, pivot faster, and skip the "no market need" graveyard.
            </p>
            <button 
              onClick={() => onLogin('register')}
              className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-8 py-3.5 text-lg font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30 transition-all shadow-lg relative z-10"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </div>

      <Footer variant="public" onShowFeatures={onShowFeatures} onShowPricing={onShowPricing} onShowAbout={onShowAbout} onShowContact={onShowContact} onShowPrivacy={onShowPrivacy} onShowTerms={onShowTerms} />
    </div>
  )
}

Homepage.propTypes = {
  onLogin: PropTypes.func.isRequired,
  onShowFeatures: PropTypes.func.isRequired,
  onShowPricing: PropTypes.func.isRequired,
  onShowAbout: PropTypes.func,
  onShowContact: PropTypes.func,
  onShowPrivacy: PropTypes.func,
  onShowTerms: PropTypes.func,
  isActive: PropTypes.bool,
}

export default Homepage
